import { useCallback, useState, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import type {
  CompoundRecordVersionSearchCondition,
  CompoundRecordVersionSortField,
  SortDirection,
} from "~/features/compoundRecordVersions/types";
import { v2Route } from "~/v2/routes";
import { CompoundRecordVersionsPresenter } from "./compoundRecordVersions.presenter";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT: CompoundRecordVersionSortField = "createdAt";
const DEFAULT_DIRECTION: SortDirection = "desc";

export function CompoundRecordVersionsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.compoundRecordVersions);
  const [isPending, startTransition] = useTransition();
  const [createOpen, setCreateOpen] = useState(false);

  // Search conditions + sort live in local state only (not URL), matching the composers feature.
  const [searchConditions, setSearchConditions] = useState<CompoundRecordVersionSearchCondition[]>([]);
  const [sortField, setSortField] = useState<CompoundRecordVersionSortField>(DEFAULT_SORT);
  const [direction, setDirection] = useState<SortDirection>(DEFAULT_DIRECTION);

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

  const onConditionsChange = useCallback((next: CompoundRecordVersionSearchCondition[]) => {
    setSearchConditions(next);
  }, []);

  const onSortingChange = useCallback(
    (nextField: CompoundRecordVersionSortField, nextDirection: SortDirection) => {
      startTransition(() => {
        setSortField(nextField);
        setDirection(nextDirection);
        setParams((prev) => ({ ...prev, page: undefined }));
      });
    },
    [setParams],
  );

  const onPageReset = useCallback(() => setParams((prev) => ({ ...prev, page: undefined })), [setParams]);

  const onPageIndexChange = useCallback(
    (next: number) => startTransition(() => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }))),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      startTransition(() =>
        setParams((prev) => ({
          ...prev,
          pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
          page: undefined,
        })),
      ),
    [setParams],
  );

  return (
    <CompoundRecordVersionsPresenter
      searchConditions={searchConditions}
      onConditionsChange={onConditionsChange}
      onPageReset={onPageReset}
      sortField={sortField}
      direction={direction}
      onSortingChange={onSortingChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      createOpen={createOpen}
      onOpenCreate={() => setCreateOpen(true)}
      onCreateOpenChange={setCreateOpen}
    />
  );
}
